import axios from 'axios';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { Reject } from '../Reject';
import { generateHeaders } from '../../utils/APIUtils';
import { RootState } from '../index';
import { logout } from './auth';
import { handleApiError } from '../handleApiError';

interface FeedbackState {
    sendingFeedback: boolean;
    feedbackSendError: boolean;
    feedbackSendSuccess: boolean;
    feedbackSendData: SendFeedbackResponse;
    registeringNotifications: boolean;
    registerNotificationsError: boolean;
    registerNotificationsSuccess: boolean;
    registerNotificationsData: RegisterSpotsNotificationResp;
}

const initialState: FeedbackState = {
    sendingFeedback: false,
    feedbackSendError: false,
    feedbackSendSuccess: false,
    feedbackSendData: undefined,
    registeringNotifications: false,
    registerNotificationsError: false,
    registerNotificationsSuccess: false,
    registerNotificationsData: undefined,
};

interface SendFeedbackParams {
    name: string;
    email: string;
    message: string;
    captchaToken: string;
}

interface SendFeedbackResponse {
    feedbackSent: boolean;
    id: string;
}

export const sendFeedback = createAsyncThunk<SendFeedbackResponse, SendFeedbackParams, Reject>(
    'sendFeedback',
    async (params, thunkAPI) => {
        const { name, email, message, captchaToken } = params;
        const { auth } = thunkAPI.getState() as RootState;
        const headers = generateHeaders(auth.jwt);
        return axios
            .post<SendFeedbackResponse>(
                `${process.env.REACT_APP_API_URL}/feedback`,
                {
                    name,
                    email,
                    message,
                    captchaToken
                },
                {
                    headers,
                }
            )
            .then((response) => {
                return response.data;
            })
            .catch((error) => {
                const apiError = handleApiError(error);
                return thunkAPI.rejectWithValue(apiError);
            });
    });

export interface RegisterSpotsNotificationParams {
    swLat: number;
    swLng: number;
    neLat: number;
    neLng: number;
    email?: string;
    captchaToken: string;
}

interface RegisterSpotsNotificationResp {
    notificationSaved: boolean;
    id: string;
}

export const registerSpotsNotification = createAsyncThunk<
    RegisterSpotsNotificationResp,
    RegisterSpotsNotificationParams,
    Reject
>('registerSpotsNotification',
    async (params, thunkAPI) => {
        const { auth } = thunkAPI.getState() as RootState;
        const headers = generateHeaders(auth.jwt);
        return axios
            .post<RegisterSpotsNotificationResp>(
                `${process.env.REACT_APP_API_URL}/notifications`,
                {
                    ...params,
                },
                {
                    headers,
                }
            )
            .then((response) => response.data)
            .catch((error) => {
                const apiError = handleApiError(error);
                return thunkAPI.rejectWithValue(apiError);
            });
    });

const feedbackSlice = createSlice({
    name: 'feedback',
    initialState,
    reducers: {
        clearFeedback: () => ({ ...initialState }),
        clearRegisterSpots: () => ({ ...initialState }),
    },
    extraReducers: (builder) => {
        builder
            .addCase(registerSpotsNotification.pending, (state) => {
                state.registeringNotifications = true;
                state.registerNotificationsError = false;
                state.registerNotificationsSuccess = false;
                delete state.registerNotificationsData;
            })
            .addCase(registerSpotsNotification.rejected, (state) => {
                state.registeringNotifications = false;
                state.registerNotificationsError = true;
                state.registerNotificationsSuccess = false;
            })
            .addCase(registerSpotsNotification.fulfilled, (state, action) => {
                state.registeringNotifications = false;
                state.registerNotificationsError = false;
                state.registerNotificationsSuccess = true;
                state.registerNotificationsData = action.payload;
            })
            .addCase(sendFeedback.pending, (state) => {
                state.sendingFeedback = true;
                state.feedbackSendError = false;
                state.feedbackSendSuccess = false;
                delete state.feedbackSendData;
            })
            .addCase(sendFeedback.rejected, (state) => {
                state.sendingFeedback = false;
                state.feedbackSendError = true;
                state.feedbackSendSuccess = false;
            })
            .addCase(sendFeedback.fulfilled, (state, action) => {
                state.sendingFeedback = false;
                state.feedbackSendError = false;
                state.feedbackSendSuccess = true;
                state.feedbackSendData = action.payload;
            })
            .addCase(logout.toString(), () => ({ ...initialState }));
    },
});

export const { clearFeedback, clearRegisterSpots } = feedbackSlice.actions;
export default feedbackSlice.reducer;
